import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import '../styles/PokemonCardsPage.css';
import layer1 from '../assets/images/cardsTemplates/layer1.webp';
import layer2 from '../assets/images/cardsTemplates/layer2.webp';
import layer3 from '../assets/images/cardsTemplates/layer3.webp';
import layer1Fallback from '../assets/images/cardsTemplates/layer1.png';
import layer2Fallback from '../assets/images/cardsTemplates/layer2.png';
import layer3Fallback from '../assets/images/cardsTemplates/layer3.png';
import { useLanguage } from '../i18n/LanguageContext';
import Picture from '../components/Picture';
import { PlayIcon, SlidersHorizontalIcon } from '@phosphor-icons/react';
import PokemonCardsRequestModal from '../components/PokemonCardsRequestModal';
import MediaLightbox from '../components/MediaLightbox';

const PRODUCTS_PUBLIC_ENDPOINT = '/.netlify/functions/products-public';

const normalizeMediaUrl = (value) => {
    const raw = String(value || '').trim();
    if (!raw) {
        return '';
    }
    if (
        raw.includes('/product-media/') &&
        !raw.includes('/.netlify/functions/product-media/')
    ) {
        return raw.replace(
            '/product-media/',
            '/.netlify/functions/product-media/'
        );
    }
    return raw;
};

const isVideoUrl = (value) => {
    const decoded = decodeURIComponent(String(value || '').toLowerCase());
    return (
        decoded.includes('.mp4') ||
        decoded.includes('.mov') ||
        decoded.includes('.webm') ||
        decoded.includes('.m4v')
    );
};

function PokemonCardsPage() {
    const { t, language } = useLanguage();
    const [activeImageIndex, setActiveImageIndex] = useState({});
    const [availabilityFilter, setAvailabilityFilter] = useState('all');
    const [cards, setCards] = useState([]);
    const features = t('cards.features');
    const steps = t('cards.process.steps');
    const optionLabels = t('cards.optionLabels');
    const locale = language === 'en' ? 'en-GB' : 'fr-FR';
    const priceFormatter = useMemo(
        () =>
            new Intl.NumberFormat(locale, {
                style: 'currency',
                currency: 'EUR',
                maximumFractionDigits: 0
            }),
        [locale]
    );
    const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
    const [modalInitialSearch, setModalInitialSearch] = useState('');
    const [toastMessage, setToastMessage] = useState('');
    const [isToastVisible, setIsToastVisible] = useState(false);
    const toastTimeoutRef = useRef(null);
    const [lightboxState, setLightboxState] = useState({
        open: false,
        items: [],
        index: 0
    });

    const openRequestModal = () => {
        setModalInitialSearch('');
        setIsRequestModalOpen(true);
    };
    const closeRequestModal = () => {
        setIsRequestModalOpen(false);
        setModalInitialSearch('');
    };

    const showToast = useCallback((message) => {
        if (toastTimeoutRef.current) {
            clearTimeout(toastTimeoutRef.current);
        }
        setToastMessage(message);
        setIsToastVisible(true);
        toastTimeoutRef.current = setTimeout(() => {
            setIsToastVisible(false);
        }, 3200);
    }, []);

    useEffect(() => {
        return () => {
            if (toastTimeoutRef.current) {
                clearTimeout(toastTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        let ignore = false;
        const fetchCards = async () => {
            try {
                const res = await fetch(
                    `${PRODUCTS_PUBLIC_ENDPOINT}?category=cartes&order=created_asc`
                );
                if (!res.ok) {
                    return;
                }
                const payload = await res.json();
                if (ignore) {
                    return;
                }
                const next = (payload.items || []).map((item) => ({
                    id: item.slug || String(item.id),
                    title: item.title,
                    price: item.price,
                    isSold: Boolean(item.is_sold),
                    buyLink: item.buy_link || '',
                    options: Array.isArray(item.options) ? item.options : [],
                    media: Array.isArray(item.media)
                        ? item.media.map(normalizeMediaUrl).filter(Boolean)
                        : []
                }));
                setCards(next);
            } catch {
                // no-op fallback
            }
        };
        fetchCards();
        return () => {
            ignore = true;
        };
    }, []);

    const getPriceLabel = (price) => {
        if (!price) {
            return t('cards.quote');
        }
        return priceFormatter.format(price);
    };

    const handleThumbnailClick = (id, index) => {
        setActiveImageIndex((prev) => ({ ...prev, [id]: index }));
    };

    const openLightbox = (items, index) => {
        if (!items.length) {
            return;
        }
        setLightboxState({
            open: true,
            items,
            index
        });
    };

    const closeLightbox = () => {
        setLightboxState((prev) => ({ ...prev, open: false }));
    };

    const navigateLightbox = (direction) => {
        setLightboxState((prev) => {
            const count = prev.items.length;
            if (!count) {
                return prev;
            }

            const delta = direction === 'next' ? 1 : -1;
            const nextIndex =
                ((prev.index + delta) % count + count) % count;

            return {
                ...prev,
                index: nextIndex
            };
        });
    };

    const filteredCards = cards.filter((item) => {
        if (availabilityFilter === 'available') {
            return !item.isSold;
        }
        if (availabilityFilter === 'sold') {
            return item.isSold;
        }
        return true;
    });

    const isSingleProduct = filteredCards.length === 1;

    return (
        <div className='page cards-page'>
            <Header />
            <main>
                <section className='hero cards-hero'>
                    <div className='container hero-grid'>
                        <div className='hero-copy'>
                            <p className='eyebrow'>{t('cards.hero.eyebrow')}</p>
                            <h1>{t('cards.hero.title')}</h1>
                            <p className='lead'>
                                {t('cards.hero.lead')}
                            </p>
                            <div className='hero-actions'>
                                <a
                                    className='button'
                                    href='mailto:lucasmafrica57@gmail.com'
                                    data-track-label='Cards hero contact link'
                                >
                                    {t('cards.hero.contact')}
                                </a>
                                <button
                                    type='button'
                                    className='button button-outline'
                                    onClick={openRequestModal}
                                    data-track-label='Open cards request modal'
                                >
                                    {t('cards.request.openButton')}
                                </button>
                            </div>
                        </div>
                        <div className='cards-visual'>
                            <div className='cards-stack'>
                                <div className='card-layer card-layer-1'>
                                    <Picture
                                        webpSrc={layer1}
                                        fallbackSrc={layer1Fallback}
                                        alt='Layer 1'
                                        className='picture-fill'
                                    />
                                </div>
                                <div className='card-layer card-layer-2'>
                                    <Picture
                                        webpSrc={layer2}
                                        fallbackSrc={layer2Fallback}
                                        alt='Layer 2'
                                        className='picture-fill'
                                    />
                                </div>
                                <div className='card-layer card-layer-3'>
                                    <Picture
                                        webpSrc={layer3}
                                        fallbackSrc={layer3Fallback}
                                        alt='Layer 3'
                                        className='picture-fill'
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className='section'>
                    <div className='container section-header'>
                        <div>
                            <p className='eyebrow'>
                                {t('cards.signature.eyebrow')}
                            </p>
                            <h2>{t('cards.signature.title')}</h2>
                        </div>
                        <p className='section-note'>
                            {t('cards.signature.note')}
                        </p>
                    </div>
                    <div className='container feature-grid'>
                        {features.map((feature, index) => (
                            <div
                                key={feature.title}
                                className='card feature-card reveal'
                                style={{ '--delay': `${index * 0.12}s` }}
                            >
                                <h3>{feature.title}</h3>
                                <p>{feature.description}</p>
                            </div>
                        ))}
                    </div>
                </section>

                <section className='section product-section'>
                    <div className='container section-header'>
                        <div>
                            <p className='eyebrow'>
                                {t('cards.collection.eyebrow')}
                            </p>
                            <h2>{t('cards.collection.title')}</h2>
                        </div>
                        <p className='section-note'>
                            {t('cards.collection.note')}
                        </p>
                    </div>
                    <div className='container filter-bar'>
                        <button
                            type='button'
                            className={`filter-button ${
                                availabilityFilter === 'all' ? 'active' : ''
                            }`}
                            onClick={() => setAvailabilityFilter('all')}
                            data-track-label='Afficher toutes les cartes'
                        >
                            {t('cards.filters.all')}
                        </button>
                        <button
                            type='button'
                            className={`filter-button ${
                                availabilityFilter === 'available'
                                    ? 'active'
                                    : ''
                            }`}
                            onClick={() =>
                                setAvailabilityFilter('available')
                            }
                            data-track-label='Afficher disponibles cartes'
                        >
                            {t('cards.filters.available')}
                        </button>
                        <button
                            type='button'
                            className={`filter-button ${
                                availabilityFilter === 'sold' ? 'active' : ''
                            }`}
                            onClick={() => setAvailabilityFilter('sold')}
                            data-track-label='Afficher vendues cartes'
                        >
                            {t('cards.filters.sold')}
                        </button>
                    </div>
                    <div
                        className={`container product-grid${
                            isSingleProduct ? ' is-single' : ''
                        }`}
                    >
                        {filteredCards.map((item, index) => {
                            const mediaItems = (item.media || [])
                                .map((mediaUrl, mediaIndex) => ({
                                    type: isVideoUrl(mediaUrl) ? 'video' : 'image',
                                    src: mediaUrl,
                                    label: t(
                                        isVideoUrl(mediaUrl)
                                            ? 'cards.media.videoLabel'
                                            : 'cards.media.imageLabel',
                                        {
                                            title: item.title,
                                            index: mediaIndex + 1
                                        }
                                    )
                                }))
                                .filter(Boolean);
                            const hasMultipleMedia = mediaItems.length > 1;
                            const rawIndex = activeImageIndex[item.id] ?? 0;
                            const selectedIndex = mediaItems.length
                                ? ((rawIndex % mediaItems.length) +
                                      mediaItems.length) %
                                  mediaItems.length
                                : 0;
                            const mainMedia =
                                mediaItems[selectedIndex] || mediaItems[0];
                            const hasExternalLink =
                                item.buyLink && item.buyLink.startsWith('http');
                            const optionTags = (item.options || []).map(
                                (option) => optionLabels[option] || option
                            );

                            return (
                                <article
                                    key={item.id}
                                    className='product-card reveal'
                                    style={{ '--delay': `${index * 0.08}s` }}
                                >
                                    <div className='product-media'>
                                        {mainMedia ? (
                                            mainMedia.type === 'video' ? (
                                                <video
                                                    src={mainMedia.src}
                                                    autoPlay
                                                    loop
                                                    muted
                                                    playsInline
                                                    preload='metadata'
                                                />
                                            ) : (
                                                <img
                                                    src={mainMedia.src}
                                                    alt={mainMedia.label}
                                                    className='picture-fill'
                                                />
                                            )
                                        ) : null}
                                        <div className='product-badge'>
                                            <span
                                                className={`status ${
                                                    item.isSold
                                                        ? 'sold'
                                                        : 'available'
                                                }`}
                                            >
                                                {item.isSold
                                                    ? t('cards.status.sold')
                                                    : t('cards.status.available')}
                                            </span>
                                        </div>
                                        {mediaItems.length ? (
                                            <button
                                                type='button'
                                                className='media-trigger'
                                                onClick={() =>
                                                    openLightbox(
                                                        mediaItems,
                                                        selectedIndex
                                                    )
                                                }
                                                aria-label={t(
                                                    'common.lightbox.openLabel'
                                                )}
                                            />
                                        ) : null}
                                    </div>
                                    <div
                                        className={`product-thumbs ${
                                            hasMultipleMedia ? '' : 'is-empty'
                                        }`}
                                        aria-hidden={!hasMultipleMedia}
                                    >
                                        {hasMultipleMedia
                                            ? mediaItems.map((media, mediaIndex) => (
                                                  <button
                                                      key={`${item.id}-${mediaIndex}`}
                                                      type='button'
                                                      className={`thumb-button ${
                                                          selectedIndex ===
                                                          mediaIndex
                                                              ? 'active'
                                                              : ''
                                                      }`}
                                                      onClick={() =>
                                                          handleThumbnailClick(
                                                              item.id,
                                                              mediaIndex
                                                          )
                                                      }
                                                      aria-label={t(
                                                          'cards.media.viewLabel',
                                                          { label: media.label }
                                                      )}
                                                  >
                                                      {media.type === 'video' ? (
                                                          <div className='thumb-video'>
                                                              <video
                                                                  src={media.src}
                                                                  muted
                                                                  playsInline
                                                                  preload='metadata'
                                                              />
                                                              <span className='thumb-play'>
                                                                  <PlayIcon
                                                                      size={14}
                                                                      weight='regular'
                                                                  />
                                                              </span>
                                                          </div>
                                                      ) : (
                                                          <img
                                                              src={media.src}
                                                              alt={media.label}
                                                          />
                                                      )}
                                                  </button>
                                              ))
                                            : null}
                                    </div>
                                    <div className='product-body'>
                                        <div className='product-header'>
                                            <h3>{item.title}</h3>
                                            {!item.isSold ? (
                                                <span className='product-price'>
                                                    {getPriceLabel(item.price)}
                                                </span>
                                            ) : null}
                                        </div>
                                        {optionTags.length > 0 ? (
                                            <div className='product-tags'>
                                                {optionTags.map((option) => (
                                                    <span
                                                        key={option}
                                                        className='tag'
                                                    >
                                                        {option}
                                                    </span>
                                                ))}
                                            </div>
                                        ) : null}
                                        {hasExternalLink && !item.isSold ? (
                                            <div className='product-actions'>
                                                <a
                                                    className='button button-outline'
                                                    href={item.buyLink}
                                                    target='_blank'
                                                    rel='noreferrer'
                                                >
                                                    {t('cards.buy')}
                                                </a>
                                            </div>
                                        ) : null}
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                </section>

                <section className='section'>
                    <div className='container section-header'>
                        <div>
                            <p className='eyebrow'>
                                {t('cards.process.eyebrow')}
                            </p>
                            <h2>{t('cards.process.title')}</h2>
                        </div>
                    </div>
                    <div className='container steps-grid'>
                        {steps.map((step, index) => (
                            <div
                                key={step.title}
                                className='card step-card reveal'
                                style={{ '--delay': `${index * 0.1}s` }}
                            >
                                <span className='step-index'>0{index + 1}</span>
                                <h3>{step.title}</h3>
                                <p>{step.description}</p>
                            </div>
                        ))}
                    </div>
                    <div className='container options-note'>
                        <div className='options-card'>
                            <span className='options-icon' aria-hidden='true'>
                                <SlidersHorizontalIcon
                                    size={18}
                                    weight='regular'
                                />
                            </span>
                            <div>
                                <span className='options-title'>
                                    {t('cards.process.optionsTitle')}
                                </span>
                                <p>{t('cards.process.options')}</p>
                            </div>
                        </div>
                    </div>
                </section>

                <section className='section'>
                    <div className='container cta-panel'>
                        <div>
                            <p className='eyebrow'>
                                {t('cards.cta.eyebrow')}
                            </p>
                            <h2>{t('cards.cta.title')}</h2>
                            <p>{t('cards.cta.note')}</p>
                        </div>
                        <a
                            className='button'
                            href='mailto:lucasmafrica57@gmail.com'
                        >
                            {t('cards.cta.contact')}
                        </a>
                        <button
                            type='button'
                            className='button button-outline'
                            onClick={openRequestModal}
                        >
                            {t('cards.request.openButton')}
                        </button>
                    </div>
                </section>
                <PokemonCardsRequestModal
                    open={isRequestModalOpen}
                    onClose={closeRequestModal}
                    initialSearch={modalInitialSearch}
                    onSuccess={showToast}
                />
            </main>
            <Footer />
            <div
                className={`toast ${isToastVisible ? 'show' : ''}`}
                role='status'
                aria-live='polite'
            >
                {toastMessage}
            </div>
            <MediaLightbox
                open={lightboxState.open}
                mediaItems={lightboxState.items}
                activeIndex={lightboxState.index}
                onClose={closeLightbox}
                onNavigate={navigateLightbox}
            />
        </div>
    );
}

export default PokemonCardsPage;
